import type { ReactNode } from "react";
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";
import { Button } from "@/components/shared/ui/Button";
import { BackgroundOrbs } from "@/components/shared/ui/BackgroundOrbs";
import { revenirSurLaBonneVoie } from "@/lib/reconnect";
import { reloadNow } from "@/lib/app-version";
import { cn } from "@/lib/utils";

interface ErrorFallbackProps {
  error?: Error | null;
  errorInfo?: React.ErrorInfo | null;
  onRetry?: () => void;
  title?: string;
  description?: ReactNode;
  /** Référence courte de l'événement Sentry, à citer au support. */
  reference?: string;
  fullScreen?: boolean;
}

// Un crash de rendu n'est pas rattrapable par un simple `refetch` : on propose donc les
// deux issues d'emblée (nouvel essai, puis reconnexion propre), contrairement à
// `ErrorState` qui n'escalade qu'après un réessai infructueux.
const DEFAULT_DESCRIPTION = (
  <>
    Pas d'inquiétude, ça se règle en un instant.
    <br />
    Clique ci-dessous pour te reconnecter.
    <br />
    <br />
    Si le souci persiste, ferme complètement l'onglet, puis rouvre la plateforme, tout devrait
    rentrer dans l'ordre 💙
  </>
);

export function ErrorFallback({
  error,
  errorInfo,
  onRetry,
  title = "Oups, petit souci de connexion 👀",
  description = DEFAULT_DESCRIPTION,
  reference,
  fullScreen = false,
}: ErrorFallbackProps) {
  // `reloadNow` marque le départ : sans ça, les requêtes que notre propre rechargement annule
  // remontent comme des coupures réseau (cf. son docbloc).
  const handleRetry = onRetry ?? reloadNow;

  return (
    <div
      className={cn(
        "relative flex items-center justify-center",
        fullScreen ? "fixed inset-0 bg-white z-50" : "min-h-[400px] w-full",
      )}
    >
      <BackgroundOrbs withAccent />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="card-surface p-10 text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 ring-1 ring-rose-100">
            <AlertTriangle size={28} />
          </div>

          <h2 className="text-xl font-black uppercase tracking-tight text-[#003366]">{title}</h2>

          <p className="mx-auto mt-3 max-w-sm text-sm font-medium leading-relaxed text-gray-500">
            {description}
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button variant="primary" icon={RotateCcw} onClick={revenirSurLaBonneVoie}>
              Revenir sur la bonne voie
            </Button>
            <Button variant="outline" icon={RefreshCw} onClick={handleRetry}>
              Réessayer
            </Button>
          </div>

          {reference && (
            <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-300">
              Réf. {reference}
            </p>
          )}

          {error && import.meta.env.DEV && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600">
                Détails techniques
              </summary>
              <pre className="mt-3 max-h-48 overflow-auto rounded-xl border border-gray-100 bg-gray-50 p-4 text-[10px] leading-relaxed text-gray-600">
                {error.name}: {error.message}
                {"\n\n"}
                {error.stack}
                {errorInfo?.componentStack && (
                  <>
                    {"\n\n"}
                    {errorInfo.componentStack}
                  </>
                )}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
